import { apiFetch } from "..";
import type { AdvancePayeeRef } from "@/utils/cash-advance";
import type { PostedJournalEntry } from "@/components/ui/AccountingEntry";
import { LONG_REQUEST_TIMEOUT_MS } from "@/lib/api/network";

export interface LiquidationReportItem {
  id: number;
  reportId: number;
  date: string;
  accountCode: string;
  itemDescription: string;
  supplier: string;
  /** The receipt's reference ("SI 123"). Named `tin` for history. */
  tin: string;
  /** The supplier's TIN, for a VAT invoice's input VAT. */
  supplierTin?: string | null;
  /** A VAT invoice: input VAT is carved out of the amount when the report posts. */
  withVat?: boolean;
  amount: number;
  sortOrder: number;
}

/**
 * What a report is:
 * - PETTY_CASH: folded automatically from petty cash vouchers.
 * - CASH_ADVANCE: liquidates an employee's cash advance voucher(s).
 * - REIMBURSEMENT: money paid out of pocket, reimbursed by a voucher.
 */
export type LiquidationReportKind = "PETTY_CASH" | "CASH_ADVANCE" | "REIMBURSEMENT";

/** When less was spent than advanced: the change comes back, or stays on the advance. */
export type OverSettlement = "RETURN" | "CARRY_FORWARD";

/** A cash advance voucher taken into a report (Section 1 of the form). */
export interface LiquidationReportAdvance {
  id: number;
  disbursementVoucherId: number;
  amountApplied: number;
  disbursementVoucher: {
    id: number;
    number: string;
    amount: number;
    payeeName: string | null;
    paymentDate: string | null;
    paidAt: string | null;
    approvedAt: string | null;
    chequeNumber: string | null;
  };
}

/** Unspent cash handed back on a checked cash advance liquidation. */
export interface CashAdvanceReturn {
  id: number;
  amount: number;
  returnDate: string;
  cashAccountId: number;
  receivedById: number | null;
  note: string | null;
  fileName: string | null;
  mimeType: string | null;
  /** Opens the acknowledgment receipt; null when none was attached. */
  url: string | null;
  createdAt: string;
}

/** How a cash advance liquidation settles its advances. */
export interface CashAdvanceSettlement {
  advances: number;
  expenses: number;
  returned: number;
  /** Spent more than advanced: owed to the employee. */
  shortfall: number;
  /** Spent less than advanced: to come back, or carry forward. */
  excess: number;
  /** The excess not returned yet. */
  excessOpen: number;
  shares: { disbursementVoucherId: number; applied: number; liquidated: number; returned: number; open: number }[];
}

/** An employee's cash advance a report may still take in. */
export interface OpenCashAdvance {
  disbursementVoucherId: number;
  number: string;
  amount: number;
  releasedAt: string | null;
  liquidated: number;
  returned: number;
  remaining: number;
  /** What this report may still take from it. */
  available: number;
  status: string;
  overdue: boolean;
  dueDate: string | null;
  projectId: number | null;
  payeeName: string | null;
  description: string | null;
}

export interface LiquidationReportUser {
  id: number;
  name: string;
  position: string | null;
}

export interface LiquidationReportProject {
  id: number;
  code: string;
  name: string;
  location: {
    name: string;
    street: string | null;
    barangay: string | null;
    city: string | null;
    province: string | null;
  } | null;
}

/** The disbursement voucher already claiming a report, when one exists. */
export interface LinkedDvSummary {
  id: number;
  number: string;
  status: string | null;
}

export interface LiquidationReport {
  id: number;
  number: string;
  reportDate: string;
  status: "draft" | "prepared" | "checked" | "replenished" | "rejected" | "cancelled";
  /**
   * Set only when the list was fetched with `onlyUnlinked` AND a live (non-deleted)
   * voucher already references this report. Drives the "already used by …" state in
   * the replenishment picker — the report is still shown, just not selectable.
   */
  linkedDv?: LinkedDvSummary | null;
  /** Live DVs currently claiming this report; empty means cancel/delete is unblocked. */
  disbursementVouchers?: LinkedDvSummary[];
  requestedByName: string;
  isPettyCashReplenishment: boolean;
  kind: LiquidationReportKind;
  /** The payee (a vendor) whose advances it takes in. */
  vendorId?: number | null;
  vendor?: { id: number; vendorId: string | null; name: string; tin: string | null } | null;
  /** Only on a report from before there was one Advance type. */
  employeeStaffId: number | null;
  employee: { id: number; firstName: string; middleName: string | null; lastName: string; employeeNumber: string | null } | null;
  /** "For the Period" (YYYY-MM-DD). */
  periodFrom: string | null;
  periodTo: string | null;
  overSettlement: OverSettlement | null;
  advances?: LiquidationReportAdvance[];
  cashReturns?: CashAdvanceReturn[];
  /** The reconciliation of a CASH_ADVANCE report; null for the other kinds. */
  settlement?: CashAdvanceSettlement | null;
  /** A carry-forward report: the payee's later reports that took in its advances (detail only). */
  carriedInto?: { id: number; number: string; status: string; amountApplied: number }[];
  projectId: number | null;
  project: LiquidationReportProject | null;
  preparedById: number;
  checkedById: number | null;
  checkedAt: string | null;
  createdAt: string;
  updatedAt: string;
  preparedByUser: LiquidationReportUser;
  checkedByUser: LiquidationReportUser | null;
  items: LiquidationReportItem[];
  // Receipt (attachment) count — powers the "has attachment" list indicator.
  _count?: { receipts: number };
  // The petty cash float this report draws on, folded down from its petty cash
  // vouchers server-side (see resolveSourceFloat). Present on EVERY report response
  // (list and detail).
  //
  // null is not "unknown" — it means the report is not petty-cash-backed: someone
  // paid out of their own pocket, so a replenishing DV reimburses that person and
  // there is no fund to credit.
  sourceFloat: { id: number; name: string } | null;
  /** The petty cash vouchers folded in: a line whose reference is one opens it. */
  pettyCashVoucherRefs?: { id: number; number: string }[];
  // Which float(s) this report's replenishment should credit, derived from its folded
  // petty cash vouchers — empty when it has none to derive from (see
  // resolveLiquidationReportFloats server-side). Only present on the single-report GET,
  // not the list response.
  resolvedFloats?: { floatId: number; floatName: string; amount: number }[];
}

export interface LiquidationReportsResponse {
  reports: LiquidationReport[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateLiquidationReportData {
  reportDate: string;
  requestedByName: string;
  projectId?: number | null;
  items: {
    date: string;
    accountCode: string;
    itemDescription: string;
    supplier: string;
    tin?: string;
    supplierTin?: string | null;
    withVat?: boolean;
    amount: number;
    sortOrder?: number;
  }[];
  /** PETTY_CASH is never sent: those reports are folded by the server. */
  kind?: "CASH_ADVANCE" | "REIMBURSEMENT";
  vendorId?: number | null;
  periodFrom?: string | null;
  periodTo?: string | null;
  overSettlement?: OverSettlement | null;
  advances?: { disbursementVoucherId: number; amountApplied: number }[];
}

// A per-item receipt attachment (image or PDF). `url` is an inline presigned URL.
export interface LiquidationReceipt {
  id: number;
  sortOrder: number;
  fileName: string;
  mimeType: string;
  url: string;
}

export interface UpdateLiquidationReportData {
  reportDate?: string;
  requestedByName?: string;
  projectId?: number | null;
  items?: CreateLiquidationReportData["items"];
  kind?: CreateLiquidationReportData["kind"];
  vendorId?: number | null;
  periodFrom?: string | null;
  periodTo?: string | null;
  overSettlement?: OverSettlement | null;
  advances?: CreateLiquidationReportData["advances"];
}

export interface RecordCashReturnData {
  amount: number;
  returnDate: string;
  method: "CASH" | "BANK";
  bankAccountId?: number | null;
  note?: string;
  receipt?: File | null;
}

export interface LiquidationReportHistoryItem {
  id: number;
  action: string;
  fromStatus: string | null;
  toStatus: string | null;
  comment: string | null;
  createdAt: string;
  user?: { id: number; name: string; position?: string } | null;
}

export const liquidationReportsApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    isPettyCashReplenishment?: boolean;
    /** Only reports without an existing (non-deleted) disbursement voucher. */
    onlyUnlinked?: boolean;
    /** When onlyUnlinked, still include the report linked to this DV (the one being edited). */
    excludeDvId?: number;
  }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set("page", String(params.page));
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.search) query.set("search", params.search);
    if (params?.status && params.status !== "all") query.set("status", params.status);
    if (params?.isPettyCashReplenishment !== undefined) {
      query.set("isPettyCashReplenishment", String(params.isPettyCashReplenishment));
    }
    if (params?.onlyUnlinked) query.set("onlyUnlinked", "true");
    if (params?.excludeDvId) query.set("excludeDvId", String(params.excludeDvId));
    const qs = query.toString();
    return apiFetch<LiquidationReportsResponse>(`/api/liquidation-reports${qs ? `?${qs}` : ""}`);
  },

  /**
   * What the report posted (its JE and any reversal). `posts` is false for a
   * cash-advance liquidation, which posts nothing — its voucher already did.
   */
  journal: (id: number) =>
    apiFetch<{ journal: PostedJournalEntry[]; postingEnabled: boolean; posts: boolean }>(
      `/api/liquidation-reports/${id}/journal`
    ),

  getById: (id: number) =>
    apiFetch<LiquidationReport>(`/api/liquidation-reports/${id}`),

  create: (data: CreateLiquidationReportData) =>
    apiFetch<LiquidationReport>("/api/liquidation-reports", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: number, data: UpdateLiquidationReportData) =>
    apiFetch<LiquidationReport>(`/api/liquidation-reports/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  submit: (id: number) =>
    apiFetch<LiquidationReport>(`/api/liquidation-reports/${id}/submit`, {
      method: "POST",
    }),

  check: (id: number, comment?: string) =>
    apiFetch<LiquidationReport>(`/api/liquidation-reports/${id}/check`, {
      method: "POST",
      body: JSON.stringify({ comment: comment ?? "" }),
    }),

  returnToDraft: (id: number, comment: string) =>
    apiFetch<LiquidationReport>(`/api/liquidation-reports/${id}/return-to-draft`, {
      method: "POST",
      body: JSON.stringify({ comment }),
    }),

  reject: (id: number, comment: string) =>
    apiFetch<LiquidationReport>(`/api/liquidation-reports/${id}/reject`, {
      method: "POST",
      body: JSON.stringify({ comment }),
    }),

  cancel: (id: number, comment: string) =>
    apiFetch<LiquidationReport>(`/api/liquidation-reports/${id}/cancel`, {
      method: "POST",
      body: JSON.stringify({ comment }),
    }),

  getHistory: (id: number) =>
    apiFetch<LiquidationReportHistoryItem[]>(`/api/liquidation-reports/${id}/history`),

  delete: (id: number) =>
    apiFetch<{ message: string }>(`/api/liquidation-reports/${id}`, {
      method: "DELETE",
    }),

  generatePdf: (id: number) =>
    apiFetch<{ pdf: string; mimeType: string }>(`/api/liquidation-reports/${id}/pdf`, {
      method: "POST",
      timeoutMs: LONG_REQUEST_TIMEOUT_MS, // headless-browser render
    }),

  // Presigned URL to the stored S3 PDF (fast — no re-render). url is null when
  // the report has no stored document yet (e.g. a draft).
  getDocument: (id: number) =>
    apiFetch<{ url: string | null }>(`/api/liquidation-reports/${id}/document`),

  // ---- Per-item receipts (image / PDF) ----

  // Upload uses native fetch + FormData (apiFetch forces JSON content-type).
  uploadReceipt: async (reportId: number, sortOrder: number, file: File): Promise<LiquidationReceipt> => {
    const formData = new FormData();
    formData.append("receipt", file);
    formData.append("sortOrder", String(sortOrder));

    const { API_URL } = await import("@/constants");
    const response = await fetch(`${API_URL}/api/liquidation-reports/${reportId}/receipts`, {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Upload failed" }));
      throw new Error(error.error || "Failed to upload receipt");
    }
    return response.json();
  },

  // ---- Cash advance liquidations ----

  /** The payee's advances a report may still take in (a vendor, or the employee of an older report). */
  getOpenAdvances: (payee: AdvancePayeeRef, exceptReportId?: number | null) => {
    const query = new URLSearchParams(
      "vendorId" in payee ? { vendorId: String(payee.vendorId) } : { employeeStaffId: String(payee.employeeStaffId) }
    );
    if (exceptReportId) query.set("exceptReportId", String(exceptReportId));
    return apiFetch<{ advances: OpenCashAdvance[] }>(`/api/liquidation-reports/advances/open?${query.toString()}`);
  },

  // Multipart (optional acknowledgment receipt), so native fetch like uploadReceipt.
  recordCashReturn: async (reportId: number, data: RecordCashReturnData): Promise<{ id: number }> => {
    const formData = new FormData();
    formData.append("amount", String(data.amount));
    formData.append("returnDate", data.returnDate);
    formData.append("method", data.method);
    if (data.method === "BANK" && data.bankAccountId) formData.append("bankAccountId", String(data.bankAccountId));
    if (data.note?.trim()) formData.append("note", data.note.trim());
    if (data.receipt) formData.append("receipt", data.receipt);

    const { API_URL } = await import("@/constants");
    const response = await fetch(`${API_URL}/api/liquidation-reports/${reportId}/returns`, {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "" }));
      throw new Error(error.error || "Failed to record the cash returned");
    }
    return response.json();
  },

  deleteCashReturn: (reportId: number, returnId: number) =>
    apiFetch<{ success: boolean }>(`/api/liquidation-reports/${reportId}/returns/${returnId}`, {
      method: "DELETE",
    }),

  getReceipts: (reportId: number) =>
    apiFetch<LiquidationReceipt[]>(`/api/liquidation-reports/${reportId}/receipts`),

  deleteReceipt: (reportId: number, receiptId: number) =>
    apiFetch<{ message: string }>(`/api/liquidation-reports/${reportId}/receipts/${receiptId}`, {
      method: "DELETE",
    }),

  // Re-key kept receipts to their item's new sortOrder + delete dropped ones (save flow).
  reconcileReceipts: (
    reportId: number,
    items: { sortOrder: number; keepReceiptIds: number[] }[],
  ) =>
    apiFetch<{ message: string; rekeyed: number; deleted: number }>(
      `/api/liquidation-reports/${reportId}/receipts/reconcile`,
      { method: "PUT", body: JSON.stringify({ items }) },
    ),
};

/**
 * One replenishment DV line per account code on the report — the same breakdown
 * the liquidation report's own expense summary shows.
 *
 * The voucher is charged to the EXPENSE codes the receipts were coded to, so the
 * payment carries its own account analysis instead of landing as a single
 * undifferentiated figure. Amounts are summed per code, so several receipts
 * sharing a code become one line.
 *
 * Codes are matched by their `code` string: a liquidation report item stores
 * `accountCode` as text and has no foreign key to the chart of accounts. A code
 * with no match (retired, or renamed since the report was written) still yields a
 * row — carrying the amount, with no finance code set — so the total continues to
 * tie back to the report and the gap is visible for someone to fix rather than
 * silently dropped.
 */
export interface ReplenishmentLine {
  accountCode: string;
  /** 0 when the code doesn't match the chart of accounts — the user must pick one. */
  financeCodeId: number;
  /** The account code's name, or the code itself when unmatched. */
  description: string;
  amount: number;
  /** With `splitVat`: the line sums VAT invoices only, else none of them. */
  isVat: boolean;
}

/**
 * `splitVat` keeps a code's VAT invoice receipts on a line of their own, marked
 * `isVat`, for a voucher that expenses the receipts itself (a reimbursement), so
 * it claims their input VAT.
 */
export function buildReplenishmentLines(
  items: Array<{ accountCode: string; amount: number; withVat?: boolean }> | undefined,
  financeCodes: Array<{ id: number; code: string; name: string }>,
  { splitVat = false }: { splitVat?: boolean } = {},
): ReplenishmentLine[] {
  const totals = new Map<string, { code: string; isVat: boolean; amount: number }>();
  for (const item of items ?? []) {
    const code = (item.accountCode ?? "").trim();
    if (!code) continue;
    const isVat = splitVat && !!item.withVat;
    const key = `${code}|${isVat ? "vat" : ""}`;
    const row = totals.get(key) ?? { code, isVat, amount: 0 };
    row.amount += item.amount || 0;
    totals.set(key, row);
  }

  const byCode = new Map(financeCodes.map((c) => [c.code, c]));

  return (
    Array.from(totals.values())
      // Numeric-aware so 5010 sorts before 5100, matching the report's own summary.
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }) || Number(a.isVat) - Number(b.isVat))
      .map(({ code, isVat, amount }) => {
        const match = byCode.get(code);
        return {
          accountCode: code,
          financeCodeId: match?.id ?? 0,
          description: match?.name ?? code,
          amount,
          isVat,
        };
      })
  );
}
